import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/ui";
import { SamsPage } from "@/components/sams/sams-page";
import { usePermissions } from "@/hooks/use-permissions";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface SamsSearch {
  /**
   * The conversation to open (`/sams?session={sessionId}`). Absent is the
   * fresh-session posture: the empty state, and a first ask that creates the
   * session implicitly. A value that cannot be a session id is dropped rather
   * than sent to the wire — the read would only 404, and an attacker-shaped
   * string would ride into a path param.
   */
  session?: string;
}

function parseSessionId(value: unknown): string | undefined {
  return typeof value === "string" && UUID_RE.test(value) ? value : undefined;
}

export const Route = createFileRoute("/_authenticated/sams/")({
  validateSearch: (search: Record<string, unknown>): SamsSearch => ({
    session: parseSessionId(search.session),
  }),
  component: SamsRoute,
});

/**
 * The SAMS route: permission-gated on the ask grant (`reports:run`) — a member
 * without it is unroutable, not just unlinked — with the session riding
 * `?session=` so a conversation is a shareable, deep-linkable URL. The route
 * file stays thin; the surface lives in `components/sams/`.
 */
function SamsRoute() {
  const { t } = useTranslation("sams");
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { session } = Route.useSearch();

  if (!can("reports", "run")) {
    return (
      <div className="p-8">
        <EmptyState title={t("denied.title")} description={t("denied.body")} variant="inline" />
      </div>
    );
  }

  return (
    <SamsPage
      sessionId={session ?? null}
      onSelectSession={(sessionId) => {
        void navigate({ to: "/sams", search: { session: sessionId }, replace: true });
      }}
      onNewSession={() => {
        void navigate({ to: "/sams", search: { session: undefined }, replace: true });
      }}
    />
  );
}
